import { useState } from "react";
import { Download, FileText, ChevronRight, BookOpen, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/shared";

const CHAPTERS = [
  {
    id: "abstract", n: "01", title: "Abstract",
    body: [
      "AUVRA RWA is a blockchain-powered platform for Real World Asset (RWA) tokenization, bridging traditional finance with decentralized infrastructure through secure, transparent and compliant issuance and management of tokenized assets on BNB Smart Chain.",
      "This whitepaper outlines the architecture, tokenomics, governance and long-term vision of the AUVRA ecosystem — the AUR utility token, the platform's institutional operating model, and the technology infrastructure supporting global participation.",
    ],
  },
  {
    id: "introduction", n: "02", title: "Introduction & Market Opportunity",
    body: [
      "Real World Assets represent a multi-trillion-dollar opportunity for tokenization. Illiquid, geographically fragmented and operationally complex asset classes — real estate, private credit, infrastructure, commodities — stand to gain the most from blockchain-native issuance, on-chain transparency and programmable distribution.",
      "AUVRA is positioned as institutional infrastructure for this transition: security-first, compliance-aligned, and built on a proven, cost-efficient network (BNB Smart Chain).",
    ],
  },
  {
    id: "platform", n: "03", title: "The AUVRA Platform",
    body: [
      "The AUVRA platform integrates: issuance modules for tokenized offerings; on-chain custody and settlement; secondary market infrastructure; investor dashboards; and compliance-aligned governance mechanisms.",
      "Each tokenized asset is designed to reflect its underlying economic rights, with clearly defined distribution schedules, transparent redemption windows and institutional-grade documentation.",
    ],
  },
  {
    id: "token", n: "04", title: "The AUR Utility Token",
    body: [
      "AUR (Aurva) is the native BEP-20 utility token of the AUVRA ecosystem. Maximum supply: 1,000,000,000 AUR. Fixed inflation.",
      "AUR powers: platform transaction fees, access to premium RWA offerings, staking and ecosystem rewards, governance participation, reduced platform service fees, institutional membership benefits and liquidity incentives.",
    ],
  },
  {
    id: "tokenomics", n: "05", title: "Tokenomics & Allocation",
    body: [
      "AUR is distributed across: Ecosystem Development, Treasury Reserve, Community Incentives, Strategic Partnerships, Team & Advisors, Liquidity, Marketing & Growth and Institutional Development.",
      "Exact percentage allocations and vesting schedules are published in the tokenomics documentation and governance proposals to ensure long-term ecosystem alignment.",
    ],
  },
  {
    id: "governance", n: "06", title: "Governance",
    body: [
      "AUR holders participate in governance across the ecosystem — protocol enhancements, platform upgrades, ecosystem incentives and strategic decisions.",
      "Governance is designed to remain aligned with regulatory expectations, institutional standards of oversight and the long-term evolution of the platform.",
    ],
  },
  {
    id: "security", n: "07", title: "Security & Compliance",
    body: [
      "AUVRA employs a defense-in-depth security architecture: independently audited smart contracts, multi-signature treasury management, secure key management, high-availability infrastructure and continuous monitoring.",
      "Compliance is central: proactive regulatory alignment, transparent operations, structured risk frameworks, and institutional-grade custody compatibility.",
    ],
  },
  {
    id: "technology", n: "08", title: "Technology Infrastructure",
    body: [
      "Built on BNB Smart Chain for its high throughput, low fees, EVM compatibility and mature ecosystem.",
      "Modular architecture supports multi-asset issuance, cross-chain readiness, and end-to-end lifecycle management — from onboarding and issuance to distributions, secondary markets and redemption.",
    ],
  },
  {
    id: "roadmap", n: "09", title: "Roadmap",
    body: [
      "Phase 01 (Live) — Foundation: core platform, smart contract framework, security architecture.",
      "Phase 02 — Expansion: multi-asset support, partner integrations, enhanced governance, regional growth.",
      "Phase 03 — Scale: institutional products, advanced automation, cross-chain readiness, ecosystem incentives.",
      "Phase 04 — Maturity: global network effects, full lifecycle tools, regulatory leadership, sustainable growth.",
    ],
  },
  {
    id: "risk", n: "10", title: "Risk Disclosures",
    body: [
      "Digital assets are subject to market, regulatory, technological and operational risks. Tokenized RWA offerings may involve illiquidity, redemption gates and jurisdictional restrictions.",
      "Participation is subject to eligibility, KYC and applicable law. Nothing herein constitutes investment, legal, tax or financial advice.",
    ],
  },
];

export default function Whitepaper({ onCta }) {
  const [active, setActive] = useState(CHAPTERS[0].id);
  return (
    <>
      <Navbar onCta={onCta} />
      <main className="pt-36 pb-24">
        <div className="container-auvra">
          {/* Hero */}
          <div className="max-w-3xl mb-16">
            <Reveal><span className="kicker mb-4 block">Whitepaper</span></Reveal>
            <Reveal i={1}><h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05]">The AUVRA <span className="text-auvra-blue">Whitepaper</span></h1></Reveal>
            <Reveal i={2}><p className="mt-6 text-lg text-secondary-auvra leading-relaxed">Read the technical and strategic foundation of AUVRA RWA — architecture, tokenomics, governance and vision. Jump to any chapter or download the full PDF.</p></Reveal>
            <Reveal i={3}>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="/docs/AUVRA-RWA-Whitepaper.pdf" target="_blank" rel="noopener noreferrer" data-testid="wp-download" className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">
                  <Download className="w-4 h-4" /> Download PDF
                </a>
                <a href="/docs/AUVRA-Institutional-Master-Manual.pdf" target="_blank" rel="noopener noreferrer" data-testid="wp-manual" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-medium px-6 py-3 rounded-full transition-all hover:bg-white/5">
                  <FileText className="w-4 h-4" /> Institutional Master Manual
                </a>
              </div>
            </Reveal>
          </div>

          {/* Reader */}
          <div className="grid lg:grid-cols-12 gap-10">
            <aside className="lg:col-span-3 lg:sticky lg:top-32 lg:self-start">
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 px-2 py-2 text-xs uppercase tracking-[0.2em] text-secondary-auvra"><BookOpen className="w-4 h-4" /> Chapters</div>
                <nav className="space-y-1 mt-1" data-testid="wp-toc">
                  {CHAPTERS.map((c) => (
                    <a key={c.id} href={`#${c.id}`} onClick={() => setActive(c.id)} data-testid={`toc-${c.id}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${active === c.id ? "bg-auvra-blue/15 text-white" : "text-secondary-auvra hover:text-white hover:bg-white/[0.03]"}`}>
                      <span className="text-[10px] text-auvra-blue tracking-[0.2em]">{c.n}</span>
                      <span className="flex-1 truncate">{c.title}</span>
                      <ChevronRight className="w-3 h-3 opacity-40" />
                    </a>
                  ))}
                </nav>
              </div>
              <a href="/docs/AUVRA-RWA-Whitepaper.pdf" target="_blank" rel="noopener noreferrer" className="mt-4 flex items-center justify-between glass rounded-2xl p-4 text-sm hover:border-auvra-blue/30 transition-colors">
                <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-auvra-blue" /> Full PDF</span>
                <ExternalLink className="w-4 h-4 text-secondary-auvra" />
              </a>
            </aside>

            <article className="lg:col-span-9 space-y-16" data-testid="wp-content">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.id} i={i % 3}>
                  <section id={c.id} className="scroll-mt-32">
                    <div className="text-xs tracking-[0.2em] text-auvra-blue mb-3">CHAPTER {c.n}</div>
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">{c.title}</h2>
                    <div className="space-y-4 text-base text-white/85 leading-relaxed">
                      {c.body.map((p, j) => <p key={j}>{p}</p>)}
                    </div>
                  </section>
                </Reveal>
              ))}

              <Reveal>
                <section className="glass rounded-2xl p-8 border border-auvra-blue/20">
                  <h3 className="text-2xl font-semibold tracking-tight mb-3">Continue with the full PDF</h3>
                  <p className="text-sm text-secondary-auvra mb-6">Download the complete whitepaper and the institutional master manual for the full technical documentation, appendices and disclosures.</p>
                  <div className="flex flex-wrap gap-3">
                    <a href="/docs/AUVRA-RWA-Whitepaper.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-5 py-2.5 rounded-full text-sm transition-all"><Download className="w-4 h-4" /> Whitepaper PDF</a>
                    <a href="/docs/AUVRA-Institutional-Master-Manual.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-medium px-5 py-2.5 rounded-full text-sm transition-all"><FileText className="w-4 h-4" /> Master Manual PDF</a>
                  </div>
                </section>
              </Reveal>
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
